import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { NurseryStockItem, NurseryCustomerTier } from '../../types/nursery';
import nurseryService from '../../services/nursery';
import PbrMark from '../cultivar/PbrMark';
import { CultivarInfoDialog } from './CultivarInfoDialog';
import { toNum, type Numeric } from '@/utils/numeric';

export interface StockItemCardProps {
  stockItem: NurseryStockItem;
  /** When true (nursery admin), shows an inline editor for the website price. */
  editable?: boolean;
  /** Customer tiers for the per-tier visibility editor (admin only). */
  tiers?: NurseryCustomerTier[];
  onUpdated?: (updated: NurseryStockItem) => void;
}

function formatPrice(value: Numeric | undefined): string | null {
  const n = toNum(value);
  if (n == null) return null;
  return n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
}

export function StockItemCard({ stockItem, editable = false, tiers = [], onUpdated }: StockItemCardProps) {
  const { t } = useTranslation('nursery', { keyPrefix: 'StockItemCard' });
  const navigate = useNavigate();
  // Merchandise is named, has no cultivar, and is not sold on the website.
  const merch = stockItem.kind === 'merchandise';
  const label = merch
    ? (stockItem.name ?? '')
    : [stockItem.cultivar_name, stockItem.rootstock_name].filter(Boolean).join(' / ');
  const typeLabel = merch
    ? (stockItem.unit ? t('soldBy', { unit: stockItem.unit }) : '')
    : stockItem.tree_type_name
      ? `${stockItem.tree_type_code} — ${stockItem.tree_type_name}`
      : (stockItem.tree_type_code ?? '');
  const stockTypeBadge = merch
    ? t('merchandise')
    : stockItem.stock_type === 'B' ? t('bareroot') : stockItem.stock_type === 'P' ? t('container') : '';
  const priceLabel = formatPrice(stockItem.website_price);

  const [editing, setEditing] = useState(false);
  const [priceInput, setPriceInput] = useState<string>(stockItem.website_price != null ? String(stockItem.website_price) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);

  // Per-tier visibility (allowlist). Empty allowlist => visible to all tiers.
  const [visOpen, setVisOpen] = useState(false);
  const [visLoaded, setVisLoaded] = useState(false);
  const [allowedTierIds, setAllowedTierIds] = useState<number[]>([]);
  const [visSaving, setVisSaving] = useState(false);

  const openVisibility = async () => {
    setVisOpen((v) => !v);
    if (!visLoaded) {
      try {
        const res = await nurseryService.getStockItemTierRules(stockItem.id);
        if (res.success) setAllowedTierIds(res.data || []);
      } catch { /* ignore */ }
      setVisLoaded(true);
    }
  };

  const toggleTier = (tierId: number) => {
    setAllowedTierIds((prev) => prev.includes(tierId) ? prev.filter((id) => id !== tierId) : [...prev, tierId]);
  };

  const saveVisibility = async () => {
    setVisSaving(true);
    try {
      await nurseryService.setStockItemTierRules(stockItem.id, allowedTierIds);
      setVisOpen(false);
    } finally {
      setVisSaving(false);
    }
  };

  const savePrice = async () => {
    setSaving(true);
    setError(null);
    try {
      const trimmed = priceInput.trim();
      const website_price = trimmed === '' ? null : Number(trimmed);
      if (website_price != null && (Number.isNaN(website_price) || website_price < 0)) {
        setError(t('invalidPrice'));
        setSaving(false);
        return;
      }
      const res = await nurseryService.updateStockItem(stockItem.id, { website_price });
      if (res.success) {
        onUpdated?.(res.data as NurseryStockItem);
        setEditing(false);
      } else {
        setError((res as { error?: { message?: string } }).error?.message || t('saveFailed'));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 hover:shadow-md transition-shadow">
      {/* Only nursery admins can open the SKU editor, so viewers get a plain
          (non-clickable) card rather than a link into a licence wall. */}
      <button
        type="button"
        disabled={!editable}
        onClick={() => navigate(`/nursery/stock-items/${stockItem.id}/edit`)}
        className="text-left w-full focus:outline-none disabled:cursor-default"
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <p className="font-semibold text-neutral-800 text-sm leading-tight">
            {label || t('stockNumber', { id: stockItem.id })}
            <PbrMark status={stockItem.cultivar_protection_status} />
          </p>
          {stockTypeBadge && (
            <span className="flex-shrink-0 text-xs px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600">
              {stockTypeBadge}
            </span>
          )}
        </div>
        {stockItem.crop_type_name && <p className="text-xs font-medium text-primary-700 mb-1">{stockItem.crop_type_name}</p>}
        {stockItem.category_name && <p className="text-xs text-neutral-500 mb-1">{t('category', { name: stockItem.category_name })}</p>}
        {typeLabel && <p className="text-xs text-neutral-500 mb-1">{typeLabel}</p>}
        {stockItem.sku_code && <p className="text-xs text-neutral-400">{t('sku', { sku: stockItem.sku_code })}</p>}
        {stockItem.description && <p className="text-xs text-neutral-400 mt-1 line-clamp-2">{stockItem.description}</p>}
        {!stockItem.active && (
          <span className="mt-2 inline-block text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-500">{t('inactive')}</span>
        )}
      </button>

      {/* The cultivar's and rootstock's description, for whoever is talking to a customer. */}
      {!merch && (
      <button
        type="button"
        onClick={() => setInfoOpen(true)}
        className="mt-2 text-xs text-primary-600 hover:text-primary-700 font-medium"
      >
        {t('whatToTell')}
      </button>
      )}
      {infoOpen && (
        <CultivarInfoDialog
          subject={{ stock_item_id: stockItem.id }}
          title={label || t('stockNumber', { id: stockItem.id })}
          onClose={() => setInfoOpen(false)}
        />
      )}

      {/* Website price */}
      {!merch && (
      <div className="mt-3 pt-3 border-t border-neutral-100">
        {editing ? (
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">{t('websitePrice')}</label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-neutral-400">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={priceInput}
                  onChange={(e) => setPriceInput(e.target.value)}
                  placeholder={t('offline')}
                  className="w-full border border-neutral-300 rounded-md pl-6 pr-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <button
                type="button"
                onClick={savePrice}
                disabled={saving}
                className="text-xs px-2.5 py-1.5 rounded-md bg-primary-600 text-white font-medium disabled:opacity-60"
              >
                {saving ? t('saving') : t('save')}
              </button>
              <button
                type="button"
                onClick={() => { setEditing(false); setError(null); setPriceInput(stockItem.website_price != null ? String(stockItem.website_price) : ''); }}
                className="text-xs px-2 py-1.5 rounded-md text-neutral-500 hover:bg-neutral-100"
              >
                {t('cancel')}
              </button>
            </div>
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-xs text-neutral-500">
              {priceLabel ? (
                <>{t('website')} <span className="font-medium text-neutral-800">{priceLabel}</span></>
              ) : (
                <span className="text-neutral-400">{t('notOnline')}</span>
              )}
            </span>
            {editable && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                {priceLabel ? t('editPrice') : t('setPrice')}
              </button>
            )}
          </div>
        )}
      </div>
      )}

      {/* Per-tier visibility (admin only, when tiers exist) */}
      {editable && !merch && tiers.length > 0 && (
        <div className="mt-3 pt-3 border-t border-neutral-100">
          <div className="flex items-center justify-between">
            <span className="text-xs text-neutral-500">{t('tierVisibility')}</span>
            <button
              type="button"
              onClick={openVisibility}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium"
            >
              {visOpen ? t('close') : t('edit')}
            </button>
          </div>
          {visOpen && (
            <div className="mt-2">
              {!visLoaded ? (
                <p className="text-xs text-neutral-400">{t('loading')}</p>
              ) : (
                <>
                  <p className="text-xs text-neutral-400 mb-2">
                    {allowedTierIds.length === 0
                      ? t('visibleAll')
                      : t('visibleTicked')}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {tiers.map((tier) => (
                      <label key={tier.id} className="inline-flex items-center gap-1.5 text-xs text-neutral-700">
                        <input
                          type="checkbox"
                          checked={allowedTierIds.includes(tier.id)}
                          onChange={() => toggleTier(tier.id)}
                        />
                        {tier.name}
                      </label>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={saveVisibility}
                    disabled={visSaving}
                    className="text-xs px-2.5 py-1.5 rounded-md bg-primary-600 text-white font-medium disabled:opacity-60"
                  >
                    {visSaving ? t('saving') : t('saveVisibility')}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {editable && (
        <div className="mt-3 pt-3 border-t border-neutral-100 text-right">
          <button
            type="button"
            onClick={() => navigate(`/nursery/stock-items/${stockItem.id}/edit`)}
            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
          >
            {t('editItem')}
          </button>
        </div>
      )}
    </div>
  );
}
